import React from 'react';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { some, values, isEmpty } from 'lodash';

import CustomerSupportURL from 'components/widgets/customerSupport/CustomerSupportURL';
import CustomerSupportEmail from 'components/widgets/customerSupport/CustomerSupportEmail';
import CustomerSupportPhoneNumber from 'components/widgets/customerSupport/CustomerSupportPhoneNumber';
import Typography from 'components/core/Typography/Typography';
import { getMerchantCustomerSupport } from 'redux/selectors/global';
import { useQueryParams } from 'hooks';
import { getMerchantDataByPageToken } from 'utilities/global';
import { Typographies } from 'models';

interface IProps {
  className?: string;
  title?: string;
  bottomText?: string;
  withSeparator?: boolean;
}

const CustomerSupport = ({
  className = '',
  title,
  bottomText = '',
  withSeparator = true
}: IProps) => {
  const { t } = useTranslation();
  const customerSupport = useSelector(getMerchantCustomerSupport);

  const { token } = useQueryParams() || {};
  const merchantData = getMerchantDataByPageToken(token);

  const { customerSupport: customerSupportFromStorage } = merchantData || {};
  const isCustomerSupportEmpty = some(values(customerSupport), isEmpty);
  const { email, url, phoneNumber } = !isCustomerSupportEmpty
    ? customerSupport
    : customerSupportFromStorage || {};

  if (!email && !url && !phoneNumber) return null;

  return (
    <CustomerSupportSection className={className}>
      {withSeparator && <CustomerSupportSeparator />}
      {title && (
        <>
          <StyledText name="support-text" type={Typographies.CAPTION}>
            {t(title)}
          </StyledText>
        </>
      )}
      <SupportWrapper>
        <CustomerSupportURL url={url} />
        <CustomerSupportEmail email={email} />
        <CustomerSupportPhoneNumber phoneNumber={phoneNumber} />
      </SupportWrapper>
      {bottomText && (
        <>
          <StyledText name="change-phone-text" type={Typographies.CAPTION}>
            {t(bottomText)}
          </StyledText>
        </>
      )}
    </CustomerSupportSection>
  );
};

export default CustomerSupport;

const CustomerSupportSection = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 1rem;
  text-align: center;
  margin-bottom: 1rem;
  width: 100%;
  font-size: ${({ theme }) => theme.fonts.size.extraSmall};
`;

export const CustomerSupportSeparator = styled.div`
  width: 100%;
  height: 0.1rem;
  background: ${({ theme }) => theme.colors.surface3};
  margin: 0.5rem 0 1rem 0;
`;

export const StyledText = styled(Typography)`
  text-align: center;
  color: ${({ theme }) => theme.colors.surface1};
  margin-bottom: 1rem;
`;

const SupportWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  width: 100%;
`;

export const CustomerSupportTitle = styled.div`
  font-size: ${({ theme }) => theme.fonts.size.extraSmall};
  color: ${({ theme }) => theme.colors.surface1};
  text-align: center;
`;
